import { EMAIL_SANITIZATION_REGEX, NAME_SANITIZATION_REGEX, PASSWORD_SANITIZATION_REGEX } from "constants/RegexConstants"
import CreateAccount from "interface/CreateAccount"
import { SIGN_UP_FIELD_ORDER, SignUpFieldErrors, cleanPhone, validateSignUpFields } from "utils/SignUpRules"

// Company name and job title may contain digits (the server allows them), so they are cleaned separately from names.
const ORG_TEXT_SANITIZATION_REGEX = /[^a-zA-Z0-9\s-]/g

/**
 * Validates and sanitizes user input for creating an account.
 *
 * Validation mirrors the server's rules (see utils/SignUpRules.ts) and reports ONE message per
 * invalid field in `fieldErrors`, so the form can show it next to the field before any request is made.
 * Characters the server would reject are no longer silently deleted: they are reported instead.
 *
 * @param {CreateAccount} accountData - The user input data for account creation.
 *
 * @returns {{
 *   isValid: boolean,
 *   fieldErrors: SignUpFieldErrors,
 *   errors: string[],
 *   sanitizedFirstName: string,
 *   sanitizedLastName: string,
 *   sanitizedEmail: string,
 *   sanitizedGender: string,
 *   sanitizedCountry: string,
 *   sanitizedCountryCode: string,
 *   sanitizedPhone: string,
 *   sanitizedPassword: string,
 *   sanitizedConfirmPassword: string,
 *   sanitizedCompanyName: string,
 *   sanitizedJobTitle: string
 * }} The validation result and the values to send.
 */
export const validateAndSanitizeCreateAccount = ({
    firstName,
    lastName,
    email,
    gender,
    country,
    countryCode,
    phone,
    password,
    confirmPassword,
    companyName = '',
    jobTitle = '',
} : CreateAccount) => {
    const fieldErrors: SignUpFieldErrors = validateSignUpFields({
        firstName, lastName, email, gender, country,
        countryCode: typeof countryCode === 'string' ? countryCode : '',
        phone, password, confirmPassword, companyName, jobTitle,
    })

    const sanitizedFirstName = firstName.trim().toLowerCase().replace(NAME_SANITIZATION_REGEX, '')
    const sanitizedLastName = lastName.trim().toLowerCase().replace(NAME_SANITIZATION_REGEX, '')
    const sanitizedEmail = email.trim().toLowerCase().replace(EMAIL_SANITIZATION_REGEX, '')
    const sanitizedCountry = country.trim().toLowerCase().replace(NAME_SANITIZATION_REGEX, '')
    const sanitizedCountryCode = typeof countryCode === 'string'
        ? countryCode.trim().toLowerCase().replace(NAME_SANITIZATION_REGEX, '')
        : ''
    const sanitizedPhone = cleanPhone(phone).toLowerCase()
    const sanitizedPassword = password.trim().replace(PASSWORD_SANITIZATION_REGEX, '')
    const sanitizedConfirmPassword = confirmPassword.trim().replace(PASSWORD_SANITIZATION_REGEX, '')
    const sanitizedCompanyName = typeof companyName === 'string' ? companyName.trim().toLowerCase().replace(ORG_TEXT_SANITIZATION_REGEX, '') : ''
    const sanitizedJobTitle = typeof jobTitle === 'string' ? jobTitle.trim().toLowerCase().replace(ORG_TEXT_SANITIZATION_REGEX, '') : ''

    const genderMapping: Record<string, string> = {
        male: 'Male',
        female: 'Female',
        rather_not_say: 'Rather not say'
    }
    const sanitizedGender = genderMapping[gender.toLowerCase()] || "Invalid"

    // The same messages as a list, in the order the fields appear on the form.
    const errors = SIGN_UP_FIELD_ORDER.map((field) => fieldErrors[field]).filter((message): message is string => Boolean(message))

    return {
        isValid: errors.length === 0,
        fieldErrors,
        errors,
        sanitizedFirstName,
        sanitizedLastName,
        sanitizedEmail,
        sanitizedGender,
        sanitizedCountry,
        sanitizedCountryCode,
        sanitizedPhone,
        sanitizedPassword,
        sanitizedConfirmPassword,
        sanitizedCompanyName,
        sanitizedJobTitle,
    }
}
